import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Button from '../common/Button';
import ComplianceSettingsModal from './ComplianceSettingsModal';

const ComplianceNotice = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('complianceConsent');
    if (!consent) {
      // Small delay for a professional entry
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const saveConsent = (settings) => {
    localStorage.setItem('complianceConsent', JSON.stringify({
      ...settings,
      timestamp: new Date().toISOString(),
      version: '2026.1'
    }));
    setIsVisible(false);
  };

  const handleAcceptAll = () => {
    saveConsent({
      essential: true,
      functional: true,
      analytical: true,
      marketing: true
    });
  };

  const handleDeclineAll = () => {
    saveConsent({
      essential: true,
      functional: false,
      analytical: false,
      marketing: false
    });
  };

  return (
    <>
      <AnimatePresence>
        {isVisible && (
          <motion.div
            initial={{ y: 100, opacity: 0, scale: 0.95 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 100, opacity: 0, scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            className="fixed bottom-6 left-6 right-6 z-[100] md:left-auto md:right-8 md:max-w-md"
          >
            <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-[0_20px_50px_rgba(79,70,229,0.15)] border border-indigo-50 p-6 md:p-8 overflow-hidden relative group">
              {/* Premium Indigo Gradients */}
              <div className="absolute top-0 right-0 -mt-8 -mr-8 w-32 h-32 bg-indigo-500/10 rounded-full blur-3xl group-hover:bg-indigo-500/20 transition-colors duration-500" />
              <div className="absolute bottom-0 left-0 -mb-8 -ml-8 w-24 h-24 bg-purple-500/10 rounded-full blur-2xl" />
              
              <div className="relative">
                <div className="flex flex-col gap-5">
                  <div className="flex items-center gap-4">
                    <div className="bg-gradient-to-br from-indigo-500 to-purple-600 p-3 rounded-2xl shadow-lg shadow-indigo-200">
                      <svg 
                        className="w-6 h-6 text-white" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2.5" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                      >
                        <path d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5" />
                        <circle cx="8.5" cy="8.5" r=".5" fill="currentColor" />
                        <circle cx="16" cy="15.5" r=".5" fill="currentColor" />
                        <circle cx="12" cy="12" r=".5" fill="currentColor" />
                        <circle cx="11" cy="17" r=".5" fill="currentColor" />
                        <circle cx="7" cy="14" r=".5" fill="currentColor" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-extrabold text-gray-900 tracking-tight">
                      Cookie-instellingen
                    </h3>
                  </div>

                  <p className="text-[15px] text-gray-600 leading-relaxed font-medium">
                    Wij gebruiken cookies om uw ervaring op ons platform te optimaliseren en de veiligheid te waarborgen. 
                    Met uw toestemming kunnen we Studiebijbaan.nl nog beter maken voor u.
                  </p>

                  <div className="flex flex-col gap-3 pt-2">
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Button 
                        variant="primary" 
                        size="md" 
                        className="flex-1 !rounded-2xl shadow-md hover:shadow-indigo-200"
                        onClick={handleAcceptAll}
                      >
                        Alles accepteren
                      </Button>
                      <Button 
                        variant="secondary" 
                        size="md" 
                        className="flex-1 !rounded-2xl border-gray-100 hover:bg-gray-50"
                        onClick={handleDeclineAll}
                      >
                        Alles weigeren
                      </Button>
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      className="!rounded-xl !py-2 border-indigo-100 text-indigo-500 hover:bg-indigo-50"
                      onClick={() => setIsModalOpen(true)}
                    >
                      Zelf instellen
                    </Button>
                  </div>
                  
                  <div className="text-center">
                    <a 
                      href="/compliance" 
                      className="text-[11px] font-semibold text-gray-400 hover:text-indigo-500 transition-colors underline underline-offset-8 decoration-gray-200 hover:decoration-indigo-500"
                    >
                      Lees ons volledige Cookiebeleid
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <ComplianceSettingsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={saveConsent}
      />
    </>
  );
};

export default ComplianceNotice;
